import Spinner from '../../Spinner';
import './HighPriorityFire.scss';
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import arrowIcon from '../../../assets/icons/down-arrow.png';
import PieChart from '../Fires/PieChart';
import { MapPin } from "@phosphor-icons/react";
import fireContainedUrl from '../../../assets/icons/fire-gray.png';
import fireHalfContainedUrl from '../../../assets/icons/fire-half-gray.png';
import fireCircleUrl from '../../../assets/icons/fire-red-orange.png';
import fireAlmostGray from '../../../assets/icons/fire-almost-gray.png';

export default function HighPriorityFire(props) {
    const { 
        setPanel,
        fire
    } = props;

    const [expanded, setExpanded] = useState(false);
    const [location, setLocation] = useState(null);
    const [country, setCountry] = useState(null);
    const [flagIcon, setFlagIcon] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const highPriorityFireRef = useRef(null);

    const goToFire = () => {
        setPanel({
            name: 'Fire PopUp',
            content: fire
        })
    }

    const returnFireIcon = useCallback((perContained) => {
        const pc = perContained ?? 0;
      
        if (pc < 41)          return fireCircleUrl;            // 0–40
        if (pc < 91)          return fireHalfContainedUrl;     // 41–90
        if (pc < 100)         return fireAlmostGray;           // 91–99
        /* pc === 100 */      return fireContainedUrl;         // 100
    }, []);

    const fireIcon = useMemo(() => returnFireIcon(fire.perContained), [returnFireIcon, fire.perContained]);

    const fetchLocation = async () => {
        console.log(`🌍 Fetching location for high priority fire: ${fire.name} (${fire.longitude}, ${fire.latitude})`);
        try {
          const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${fire.longitude},${fire.latitude}.json`;
  
          const { data } = await axios.get(url, {
            params: {
              access_token:
                'pk.eyJ1IjoiY2VzYXJtbG5keiIsImEiOiJjbHJ0b2JqZjEwOHpiMmtxa25ubzZ0ZXE3In0.18DfsEFZiecc6H8jHdMOIQ'
            }
          });
  
          const context = data.features?.[0]?.context || [];
          let city = '';
          let state = '';
          let countryCode = '';
  
          context.forEach((c) => {
            if (c.id.includes('place')) {
              city = c.text;
            } else if (c.id.includes('region')) {
              state = c.text;
            } else if (c.id.includes('country')) {
              countryCode = c.short_code?.toLowerCase();
            }
          });
  
          setLocation(city && state ? `${city}, ${state}` : '');
          setCountry(countryCode);
          
          // Load flag icon dynamically if country code is available
          if (countryCode) {
            loadFlagIcon(countryCode);
          }
          console.log(data)
        } catch (err) {
          console.error('Mapbox geocoding failed:', err);
          setLocation('');
          setCountry(null);
          setFlagIcon(null);
        }
    };

    const loadFlagIcon = async (countryCode) => {
        if (!countryCode) return;
        
        try {
            // Dynamically import the flag icon based on country code
            const flagModule = await import(`../../../assets/icons/flag-icons-main/flags/4x3/${countryCode}.svg`);
            setFlagIcon(flagModule.default);
        } catch (error) {
            console.log(`Flag icon for ${countryCode} not found, using fallback`);
            // If the specific flag doesn't exist, we could set a default flag or null
            setFlagIcon(null);
        }
    }

    // Intersection Observer to detect when HighPriorityFire component is visible
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !isVisible) {
                    console.log(`🔥 High Priority Fire "${fire.name}" became visible - starting location fetch`);
                    setIsVisible(true);
                }
            },
            {
                threshold: 0.1, // Trigger when 10% of the component is visible
                rootMargin: '50px' // Start loading 50px before it becomes visible
            }
        );

        if (highPriorityFireRef.current) {
            observer.observe(highPriorityFireRef.current);
        }

        return () => {
            if (highPriorityFireRef.current) {
                observer.unobserve(highPriorityFireRef.current);
            }
        };
    }, [isVisible, fire.name]);

    // Only fetch location when component becomes visible
    useEffect(() => {
        if (isVisible) {
            fetchLocation();
        }
    }, [isVisible]);

    const borderColor = fire.areas.some((a) => a.distance <= 1) ? '#e82700' : '#ffcc00';

    return (
        <div 
            className='high-priority-fire-card' 
            ref={highPriorityFireRef}
            style={{ borderLeft: `4px solid ${borderColor}` }}
        >
            <div className='high-priority-fire-card-header' onClick={() => setExpanded((prevState) => !prevState)}>
                <img src={fireIcon} style={{ width: '1.5rem' }}/>
                <div className='high-priority-fire-card-header-title' style={{  }}>{fire.name ? fire.name : 'Not Available'}</div>
                <img style={{ width: '0.9rem', filter: 'invert(100%)' }} src={arrowIcon}/>
            </div>
            {!isVisible ? 
                <div className='high-priority-fire-card-location'>
                    <div className='high-priority-fire-card-location-text'>Loading...</div>
                </div>
                :
                location ?
                <div className='high-priority-fire-card-location'>
                    <div className='high-priority-fire-card-location-inner'>
                        <div className='high-priority-fire-card-location-inner-text'>
                            {location}
                        </div>
                        {country && flagIcon && (
                            <img 
                                src={flagIcon} 
                                alt={`${country.toUpperCase()} flag`}
                                className='high-priority-fire-card-location-flag'
                            />
                        )}
                    </div>
                </div>
                :
                <Spinner />
            }
            {expanded &&
            <div className='high-priority-fire-card-expanded'>
                <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                        <div style={{ width: '50%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '0.2rem' }}>
                            <div style={{ fontSize: '1rem', color: 'black', fontWeight: 'bold',  }}>{fire.areaAC ? fire.areaAC.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'Not Available'}</div>
                            <div style={{ fontSize: '0.8rem', color: 'black',  }}>Acres Burned</div>
                        </div>
                        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                            <div style={{ fontSize: '1rem',  color: 'black', fontWeight: 'bold' }}>${fire.areas.reduce((sum, area) => sum + Number(area.value), 0).toLocaleString()}</div>
                            <div style={{ fontSize: '0.8rem', color: 'black',  }}>Total Insured Value</div>
                        </div>
                    </div>
                    <div className="fire-popup-main-right">
                        <PieChart 
                            percentage={fire.perContained}
                            size={80}
                            type='containment'
                        />
                        <div style={{ fontSize: '0.8rem', color: 'black',  }}>Containment</div>
                    </div>
                </div>
                {fire.areas.length > 0 &&
                <div className='high-priority-fire-card-expanded-areas-header'>
                    <div className='high-priority-fire-card-expanded-areas-header-text'>
                        Areas at Risk ({(fire.areas.length)})
                    </div>
                </div>}
                <div className='high-priority-fire-card-expanded-areas-list'>
                {fire.areas.length > 0 && fire.areas.map((area, index) => {
                    return (
                        <div key={index} className='high-priority-fire-card-expanded-area-item'>
                            <div className='high-priority-fire-card-expanded-area-item-name'>{area.area_name}</div>
                            <div className={`high-priority-fire-card-expanded-area-item-distance ${area.distance <= 1 ? 'critical' : 'warning'}`}>
                                {area.distance} mi
                            </div>
                        </div>
                    )
                    })}
                </div>
                <div className='high-priority-fire-card-expanded-button' onClick={goToFire}>
                    View Fire
                </div>
            </div>
            }
        </div>
    );
}
